import { useState, useContext } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  VStack,
  Text,
  Button,
  useToast,
  HStack,
  Code,
  Icon,
} from '@chakra-ui/react';
import { CustomRefreshIcon } from '../../theme/icons/CustomRefreshIcon';
import { CustomCheckIcon } from '../../theme/icons/CustomCheckIcon';
import { HttpContext } from '../../context/HttpContext';
import CustomToast from '../../common/CustomToast';

const RefreshKeyModal = ({
  isOpen,
  onClose,
  fetchData,
  webhookSubscriptionId,
}) => {
  const { authAxios } = useContext(HttpContext);
  const toast = useToast();
  const [secretKey, setSecretKey] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const rotateSecret = async () => {
    setIsRefreshing(true);
    try {
      const { data } = await authAxios.patch(
        `/api/v1/webhook-subscriptions/${webhookSubscriptionId}/rotate-secret`
      );
      const newKey = data?.data?.attributes?.secretKey;
      setSecretKey(newKey);
    } catch (err) {
      toast({
        render: ({ onClose: toastClose }) => (
          <CustomToast
            status="error"
            title="Something went wrong"
            description="Please try again later."
            onClose={toastClose}
          />
        ),
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleClose = () => {
    setSecretKey('');
    onClose();
    fetchData?.({ pageIndex: 0, pageSize: 5 });
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalBody pt={10}>
          {secretKey ? (
            <VStack spacing={6} py={4} textAlign="center">
              <Icon as={CustomCheckIcon} w={10} h={10} color="success" />
              <Text fontSize="lg" fontWeight="semibold">
                New key generated successfully!
              </Text>
              <HStack spacing={2} wrap="wrap" justify="center">
                <Text fontSize="sm">Your new key:</Text>
                <Code px={2} py={1} borderRadius="md">
                  {secretKey}
                </Code>
              </HStack>
            </VStack>
          ) : (
            <VStack spacing={6} py={4} textAlign="center">
              <Icon as={CustomRefreshIcon} w={10} h={10} />
              <Text fontSize="lg" fontWeight="semibold">
                Refresh secret key?
              </Text>
              <Text fontSize="sm" color="gray.500">
                This will invalidate the current key. The action can’t be
                undone.
              </Text>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter justifyContent="flex-end" gap={4}>
          {!secretKey && (
            <Button
              variant="link"
              color="primaryDark"
              onClick={handleClose}
              size="sm"
            >
              Cancel
            </Button>
          )}
          <Button
            onClick={secretKey ? handleClose : rotateSecret}
            isLoading={isRefreshing}
            size="sm"
          >
            {secretKey ? 'Close' : 'Refresh'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RefreshKeyModal;
